// src/theme.js
import { createTheme } from '@mui/material/styles';

// Brand colors extracted from logo
const brandColors = {
  primary: {
    main: '#FFD700',      // Gold from logo
    light: '#FFE082',
    dark: '#F57C00',
    contrastText: '#1A1A1A',
  },
  secondary: {
    main: '#1A1A1A',      // Black from logo
    light: '#424242',
    dark: '#000000',
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#FFC107',      // Lighter gold
    light: '#FFD54F',
    dark: '#F9A825',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FAFAFA',
  },
  text: {
    primary: '#1A1A1A',
    secondary: '#666666',
  },
};

// Light theme
const lightPalette = {
  mode: 'light',
  primary: {
    ...brandColors.primary,
  },
  secondary: {
    ...brandColors.secondary,
  },
  accent: {
    ...brandColors.accent,
  },
  background: {
    default: brandColors.background.default,
    paper: brandColors.background.paper,
  },
  text: {
    primary: brandColors.text.primary,
    secondary: brandColors.text.secondary,
  },
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
  },
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

// Dark theme
const darkPalette = {
  mode: 'dark',
  primary: {
    ...brandColors.primary,
  },
  secondary: {
    ...brandColors.secondary,
  },
  accent: {
    ...brandColors.accent,
  },
  background: {
    default: '#0A0A0A',      // Deep black for better contrast
    paper: '#1A1A1A',        // Dark paper with subtle warmth
  },
  text: {
    primary: '#FFFFFF',         // Pure white for primary text
    secondary: '#A0A0A0',      // Softer secondary text
    disabled: '#666666',        // Disabled text
  },
  success: {
    main: '#66BB6A',          // Brighter success for dark mode
    light: '#81C784',
    dark: '#4CAF50',
  },
  error: {
    main: '#EF5350',          // Brighter error for dark mode
    light: '#E57373',
    dark: '#F44336',
  },
  warning: {
    main: '#FFB74D',          // Brighter warning for dark mode
    light: '#FFCC80',
    dark: '#FF9800',
  },
  info: {
    main: '#42A5F5',          // Brighter info for dark mode
    light: '#64B5F6',
    dark: '#2196F3',
  },
  divider: 'rgba(255, 255, 255, 0.08)',  // Subtle divider for dark mode
  action: {
    hover: 'rgba(255, 255, 255, 0.04)',
    selected: 'rgba(255, 255, 255, 0.08)',
    disabled: 'rgba(255, 255, 255, 0.26)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
};

// Common theme settings
const themeSettings = {
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 2px 4px rgba(0, 0, 0, 0.2)'
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 4px 8px rgba(0, 0, 0, 0.25)'
            : '0 4px 8px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
        },
        focused: {
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}20`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
          },
        },
        focused: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.palette.primary.main,
            borderWidth: 2,
          },
        },
      },
    },
  },
};

// Create light and dark themes
export const lightTheme = createTheme({
  ...themeSettings,
  palette: lightPalette,
});

export const darkTheme = createTheme({
  ...themeSettings,
  palette: darkPalette,
});

export const getThemeOptions = (mode) => ({
  ...themeSettings,
  palette: mode === 'light' ? lightPalette : darkPalette,
});

// Export brand colors for use in components
export { brandColors };
